import { useEffect, useRef } from 'react';
import { Platform, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import { useAuth } from '@/contexts/AuthContext';

function buildWatermarkDataUrl(text: string, dense = false): string {
  const size = dense ? 160 : 240;
  const fontSize = dense ? 11 : 12;
  const opacity = dense ? 0.09 : 0.045;

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  ctx.clearRect(0, 0, size, size);
  ctx.save();
  ctx.translate(size / 2, size / 2);
  ctx.rotate(-Math.PI / 5);
  ctx.font = `${fontSize}px system-ui, -apple-system, sans-serif`;
  ctx.fillStyle = `rgba(201,168,67,${opacity})`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 0, 0);
  ctx.restore();

  return canvas.toDataURL();
}

// ─── Global fixed overlay ─────────────────────────────────────────────────────

export function WebWatermarkOverlay() {
  if (Platform.OS !== 'web') return null;
  return <WebWatermarkOverlayInner />;
}

function WebWatermarkOverlayInner() {
  const { user } = useAuth();
  const divRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!divRef.current || !user) return;
    const label = user.user_metadata?.full_name
      ? `${user.user_metadata.full_name} · ${user.id.slice(0, 8)}`
      : user.id.slice(0, 16);
    const url = buildWatermarkDataUrl(label, false);
    divRef.current.style.backgroundImage = `url(${url})`;
  }, [user]);

  return (
    <div
      ref={divRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        pointerEvents: 'none',
        backgroundRepeat: 'repeat',
        backgroundSize: '240px 240px',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      } as React.CSSProperties}
    />
  );
}

// ─── Sensitive content wrapper ────────────────────────────────────────────────

interface SensitiveContentProps {
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}

export function SensitiveContent({ children, style }: SensitiveContentProps) {
  if (Platform.OS !== 'web') return <View style={style}>{children}</View>;
  return <SensitiveContentInner style={style}>{children}</SensitiveContentInner>;
}

function SensitiveContentInner({ children, style }: SensitiveContentProps) {
  const { user } = useAuth();
  const divRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!divRef.current || !user) return;
    const label = user.user_metadata?.full_name
      ? `${user.user_metadata.full_name} · ${user.id.slice(0, 8)}`
      : user.id.slice(0, 16);
    const url = buildWatermarkDataUrl(label, true);
    divRef.current.style.backgroundImage = `url(${url})`;
  }, [user]);

  const flatStyle = (StyleSheet.flatten(style) ?? {}) as object;
  return (
    <div style={{ position: 'relative', ...flatStyle }}>
      {children}
      <div
        ref={divRef}
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 10,
          pointerEvents: 'none',
          backgroundRepeat: 'repeat',
          backgroundSize: '160px 160px',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        } as React.CSSProperties}
      />
    </div>
  );
}
